/**
 * fleetPermissions.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for fleet permissions on the React frontend.
 *
 * HOW IT WORKS
 * ─────────────
 * The login API stores the user's role module IDs in localStorage('modules')
 * as a comma-separated string of integers (e.g. "60,70,168,300,301,340").
 * These IDs map to rows in the `modules` table.
 *
 * This file:
 *  1. Reads all identity flags from every localStorage key the login could use
 *  2. Maps raw module IDs → fleet key strings via FLEET_MODULE_MAP
 *  3. Grants full bypass to superusers (national / admin / facility admin)
 *  4. Exports fleetCan(), fleetPerms(), fleetNavItems()
 */

// ─── Fleet module ID → key map ────────────────────────────────────────────────
// Must exactly match IDs in migration_fleet_modules.sql
const FLEET_MODULE_MAP = {
    300: 'fleet_dashboard',
    301: 'fleet_vehicles',
    302: 'fleet_notice_board',
    303: 'fleet_reports',
    310: 'fleet_maintenance_view',
    311: 'fleet_maintenance_write',
    312: 'fleet_costs_view',
    313: 'fleet_costs_write',
    320: 'fleet_trips_view',
    321: 'fleet_trips_write',
    322: 'fleet_deployments_view',
    323: 'fleet_deployments_write',
    330: 'fleet_assignments_view',
    331: 'fleet_assignments_write',
    340: 'fleet_incidents_view',
    341: 'fleet_incidents_write',
    350: 'fleet_disposals_view',
    351: 'fleet_disposals_write',
    352: 'fleet_disposals_approve_hod',
    353: 'fleet_disposals_approve_director',
    360: 'fleet_documents_view',
    361: 'fleet_documents_write',
    370: 'fleet_scope_programme',
    371: 'fleet_scope_own',
};

// ─── Read identity + modules from localStorage ────────────────────────────────
// util.setLoginInfoLocalStorage() stores data in a 'user' JSON object.
// Login.js also stores individual flat keys (is_admin, is_national, etc).
// We read from BOTH to handle either pattern.
function getUserContext() {
    try {
        // --- Parse user object stored by util ---
        const userObj = JSON.parse(localStorage.getItem('user') || '{}');

        // --- Identity flags: check flat keys first, fallback to user object ---
        const isAdmin = (
            localStorage.getItem('is_admin') === '1' ||
            localStorage.getItem('is_admin') === 'true' ||
            !!userObj.is_admin ||
            userObj.type === 'ADMIN'
        );

        const isNational = (
            localStorage.getItem('is_national') === '1' ||
            localStorage.getItem('is_national') === 'true' ||
            !!userObj.is_national ||
            (userObj.client && !!userObj.client.is_national)
        );

        const isClientAdmin = (
            localStorage.getItem('is_client_admin') === '1' ||
            localStorage.getItem('is_client_admin') === 'true' ||
            !!userObj.is_client_admin ||
            userObj.role_id === 2 ||
            userObj.role_id === '2'
        );

        const region = (
            localStorage.getItem('user_region') ||
            userObj.region ||
            ''
        );

        const isRegional = !!(region && !isAdmin && !isNational);

        // Superuser = sees everything, API also bypasses all checks for these
        const isSuperUser = isAdmin || isNational || isClientAdmin;

        // --- Module IDs: stored as CSV string of integers ---
        // e.g. "60,70,71,168,300,301,310,320,330"
        const modulesRaw = localStorage.getItem('modules') || '';
        const moduleIds  = modulesRaw
            ? modulesRaw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n))
            : [];

        // Convert IDs to fleet key strings using the map
        // Non-fleet IDs (like 60, 70, 168) just won't be in the map — that's fine
        const fleetKeys = moduleIds
            .map(id => FLEET_MODULE_MAP[id])
            .filter(Boolean);


        // Programme scope
        const programme   = userObj.assigned_programme || localStorage.getItem('user_programme') || '';
        const isProgramme = !!(fleetKeys.includes('fleet_scope_programme') && programme);
        return {
            isAdmin,
            isNational,
            isClientAdmin,
            isRegional,
            isProgramme,
            programme,
            isSuperUser,
            region,
            moduleIds,   // raw IDs for debugging
            fleetKeys,   // resolved fleet key strings
            facility: userObj.facility || userObj.business_name || '',
            name:     userObj.name     || '',
        };
    } catch (e) {
        console.warn('fleetPermissions: localStorage read error', e);
        return {
            isAdmin: false, isNational: false, isClientAdmin: false,
            isRegional: false, isSuperUser: false, region: '',
            moduleIds: [], fleetKeys: [],
        };
    }
}

// ─── Check a single fleet permission ─────────────────────────────────────────
// Usage: if (fleetCan('costs_view')) { ... }
export function fleetCan(key) {
    const ctx = getUserContext();
    // Superusers bypass all module checks — API also grants them full access
    if (ctx.isSuperUser) return true;
    return ctx.fleetKeys.includes(`fleet_${key}`);
}

// ─── Full permissions object ──────────────────────────────────────────────────
// Usage: const p = fleetPerms(); if (p.costsView) { ... }
export function fleetPerms() {
    const ctx = getUserContext();
    const { isSuperUser, fleetKeys } = ctx;

    // For superusers every flag is true; for others check the resolved key list
    const has = (key) => isSuperUser || fleetKeys.includes(key);

    return {
        // ── Identity ──────────────────────────────────────────────────────────
        isAdmin:       ctx.isAdmin,
        isNational:    ctx.isNational,
        isClientAdmin: ctx.isClientAdmin,
        isRegional:    ctx.isRegional,
        isProgramme:   ctx.isProgramme,
        programme:     ctx.programme,
        isSuperUser,
        region:        ctx.region,

        // ── Vehicles & Dashboard ──────────────────────────────────────────────
        dashboard:     has('fleet_dashboard'),
        vehicles:      has('fleet_vehicles'),
        noticeBoard:   has('fleet_notice_board'),
        reports:       has('fleet_reports'),

        // ── Maintenance & Costs ───────────────────────────────────────────────
        maintenanceView:  has('fleet_maintenance_view'),
        maintenanceWrite: has('fleet_maintenance_write'),
        costsView:        has('fleet_costs_view'),
        costsWrite:       has('fleet_costs_write'),

        // ── Trips & Deployments ───────────────────────────────────────────────
        tripsView:         has('fleet_trips_view'),
        tripsWrite:        has('fleet_trips_write'),
        deploymentsView:   has('fleet_deployments_view'),
        deploymentsWrite:  has('fleet_deployments_write'),

        // ── Drivers & Assignments ─────────────────────────────────────────────
        assignmentsView:  has('fleet_assignments_view'),
        assignmentsWrite: has('fleet_assignments_write'),

        // ── Incidents ─────────────────────────────────────────────────────────
        incidentsView:  has('fleet_incidents_view'),
        incidentsWrite: has('fleet_incidents_write'),

        // ── Disposals ─────────────────────────────────────────────────────────
        disposalsView:           has('fleet_disposals_view'),
        disposalsWrite:          has('fleet_disposals_write'),
        disposalsApproveHOD:     has('fleet_disposals_approve_hod'),
        disposalsApproveDirector:has('fleet_disposals_approve_director'),

        // ── Documents ─────────────────────────────────────────────────────────
        documentsView:  has('fleet_documents_view'),
        documentsWrite: has('fleet_documents_write'),

        // ── Data scope flags (these NEVER bypass — even for admins) ───────────
        scopeOwn:       fleetKeys.includes('fleet_scope_own'),
        scopeProgramme: fleetKeys.includes('fleet_scope_programme'),

        // ── Analytics dashboard permissions ───────────────────────────────────
        analyticsDepreciation:   has('fleet_analytics_depreciation'),
        analyticsCostPerKm:      has('fleet_analytics_cost_per_km'),
        analyticsRepairReplace:  has('fleet_analytics_repair_replace'),
        analyticsProgrammeCost:  has('fleet_analytics_programme_cost'),
        analyticsDriverScorecard:has('fleet_analytics_driver_scorecard'),
        analyticsUtilisation:    has('fleet_analytics_utilisation'),
        analyticsExec:           has('fleet_analytics_exec'),

        // ── User context (for display purposes) ───────────────────────────────
        facility: ctx.facility || '',
        name:     ctx.name     || '',
    };
}

// ─── Nav visibility helper ────────────────────────────────────────────────────
// Used by Leftnavs.js to show/hide fleet submenu items
export function fleetNavItems() {
    const p = fleetPerms();
    return {
        noticeBoard:  p.noticeBoard  || p.isSuperUser,
        auditLog:     p.isSuperUser,                    // admin/national only
        dashboard:    p.dashboard    || p.isSuperUser,
        vehicles:     p.vehicles     || p.isSuperUser,
        assignments:  p.assignmentsView,
        deployments:  p.deploymentsView,
        trips:        p.tripsView,
        maintenance:  p.maintenanceView,
        costs:        p.costsView,
        disposals:    p.disposalsView,
        incidents:    p.incidentsView,
        reports:      p.reports,
        isSuperUser:  p.isSuperUser,
        isRegional:   p.isRegional,
        isProgramme:  p.scopeProgramme,
        // ── Analytics nav items ───────────────────────────────────────────────
        analyticsDepreciation:    p.analyticsDepreciation,
        analyticsCostPerKm:       p.analyticsCostPerKm,
        analyticsRepairReplace:   p.analyticsRepairReplace,
        analyticsProgrammeCost:   p.analyticsProgrammeCost,
        analyticsDriverScorecard: p.analyticsDriverScorecard,
        analyticsUtilisation:     p.analyticsUtilisation,
        analyticsExec:            p.analyticsExec,
        // Convenience — can see any analytics dashboard
        anyAnalytics: p.analyticsDepreciation || p.analyticsCostPerKm ||
                      p.analyticsRepairReplace || p.analyticsProgrammeCost ||
                      p.analyticsDriverScorecard || p.analyticsUtilisation ||
                      p.analyticsExec || p.isSuperUser,
    };
}